import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, UploadCloud, FileText, Sliders, Sparkles, Loader2, RotateCcw, CheckCircle2 } from "lucide-react";
import { Button, Spinner } from "./ui/kit";
import { fmtKc } from "../lib/format";
import api, { apiError } from "../lib/api";
import Vyber from "./Vyber";

/**
 * Ceník dodavatele: nahraje se Excel/CSV (PDF s tabulkou přímo, sken přes AI), porovná se s ceníkem
 * a uživatel potvrdí, které ceny přepsat a které nové položky přidat. Ceník
 * drží jednu nákupní cenu; dodavatel je jen informace, od koho přišla.
 * Každý import jde vrátit.
 */
const NOMULTI = { headers: { "Content-Type": undefined } };
const POLE = [["nazev", "Název *"], ["katalogove", "Kat. číslo"], ["ean", "EAN"], ["mj", "MJ"],
  ["cena_nakup", "Cena *"], ["sleva_pct", "Sleva %"]];
const STAV = {
  ZDRAZILO: ["Zdražilo", "text-danger"], ZLEVNILO: ["Zlevnilo", "text-success"],
  BEZE_ZMENY: ["Beze změny", "text-muted"], NOVA: ["Nová", "text-accent-hover"], BEZ_CENY: ["Bez ceny", "text-muted"],
};
const FILTRY = [
  ["zmeny", "Změny cen", (x) => x.stav === "ZDRAZILO" || x.stav === "ZLEVNILO"],
  ["ZDRAZILO", "Zdražilo", (x) => x.stav === "ZDRAZILO"],
  ["ZLEVNILO", "Zlevnilo", (x) => x.stav === "ZLEVNILO"],
  ["NOVA", "Nové", (x) => x.stav === "NOVA"],
  ["BEZE_ZMENY", "Beze změny", (x) => x.stav === "BEZE_ZMENY" || x.stav === "BEZ_CENY"],
  ["vse", "Vše", () => true],
];
const NA_STRANKU = 150;
const pct = (n) => (n == null ? "" : `${n > 0 ? "+" : ""}${String(n).replace(".", ",")} %`);
const datum = (d) => new Date(d).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric" });
const lzeVybrat = (x) => ["ZDRAZILO", "ZLEVNILO", "NOVA"].includes(x.stav);
const nazevListu = (x) => (x === "*" ? "Všechny listy s cenami" : x);
const cislo = (t) => { const v = Number(String(t).replace(/\s/g, "").replace(",", ".")); return Number.isFinite(v) ? v : 0; };
const r2 = (x) => Math.round(x * 100) / 100;

export default function CenikDodavatele({ onClose, onDone }) {
  const [step, setStep] = useState(1);            // 1 soubor | 2 kontrola | 3 hotovo
  const [dodavatel, setDodavatel] = useState("");
  const [file, setFile] = useState(null);
  const [analyza, setAnalyza] = useState(null);   // Excel: sloupce, listy, mapování
  const [mapping, setMapping] = useState({});
  const [showMapping, setShowMapping] = useState(false);
  const [rows, setRows] = useState([]);
  // Ceny v souboru: NAKUP (od dodavatele) | VYROBCE (ceník výrobce = prodejní, nákup = prodej − sleva)
  const [cenyMode, setCenyMode] = useState("NAKUP");
  const [slevaDod, setSlevaDod] = useState("");
  const [surove, setSurove] = useState(null);     // řádky souboru, jak přišly (pro přepnutí režimu)
  const [par, setPar] = useState(null);           // {radky, souhrn}
  const [vybrane, setVybrane] = useState({});
  const [filtr, setFiltr] = useState("zmeny");
  const [limit, setLimit] = useState(NA_STRANKU);
  const [kategorie, setKategorie] = useState([]);
  const [kat, setKat] = useState("");
  const [marze, setMarze] = useState("");
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(false);
  const [err, setErr] = useState("");
  const [davky, setDavky] = useState([]);
  const [vysledek, setVysledek] = useState(null);
  const [vraceno, setVraceno] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const nactiDavky = () => api.get("/api/cenik-dodavatele/davky").then((r) => setDavky(r.data.items || [])).catch(() => {});
  useEffect(() => {
    nactiDavky();
    api.get("/api/meta/ciselniky").then((r) => setKategorie(r.data.kategorie || [])).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  // Řádky souboru → porovnání s ceníkem; změny cen se předvyberou, nové položky ne.
  const porovnej = async (radky, dod, mode = cenyMode, sleva = slevaDod) => {
    setSurove(radky || []);
    const k = 1 - Math.min(Math.max(cislo(sleva), 0), 99.99) / 100;
    const rs = (radky || []).map((r, i) => (mode === "VYROBCE"
      ? { ...r, _i: i, cena_prodej: r.cena_nakup, cena_nakup: r2((Number(r.cena_nakup) || 0) * k) }
      : { ...r, _i: i, cena_prodej: undefined }));
    setRows(rs);
    const { data } = await api.post("/api/cenik-dodavatele/parovani", { radky: rs, dodavatel: dod || "" });
    setPar(data);
    setVybrane(Object.fromEntries(data.radky.filter((x) => (x.stav === "ZDRAZILO" || x.stav === "ZLEVNILO") && !x.duplicita)
      .map((x) => [x.i, true])));
    const zmen = data.souhrn.ZDRAZILO + data.souhrn.ZLEVNILO;
    setFiltr(zmen ? "zmeny" : data.souhrn.NOVA ? "NOVA" : "vse");
    setLimit(NA_STRANKU); setStep(2);
  };

  const pollJob = (jobId, dod) => {
    setAi(true);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/api/import-jobs/${jobId}`);
        if (data.status === "processing") return;
        clearInterval(pollRef.current); setAi(false);
        if (data.status === "error") { setErr(data.detail || "Dokument se nepodařilo přečíst."); return; }
        const d = dod || data.result.dodavatel || "";
        setDodavatel(d);
        await porovnej(data.result.radky, d);
      } catch (e) {
        clearInterval(pollRef.current); setAi(false);
        setErr(apiError(e.response?.data?.detail) || "Čtení se nezdařilo.");
      }
    }, 2000);
  };

  const rozbor = async (f, sheet, map) => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (sheet) fd.append("sheet", sheet);
      if (map && Object.keys(map).length) fd.append("mapovani", JSON.stringify(map));
      const { data } = await api.post("/api/cenik-dodavatele/analyza", fd, NOMULTI);
      if (data.rezim === "AI") { pollJob(data.job_id, dodavatel.trim()); return; }
      const dod = dodavatel.trim() || data.dodavatel || "";
      setDodavatel(dod);
      setAnalyza(data); setMapping(data.navrh_mapovani || {});
      await porovnej(data.radky, dod);
    } catch (e) { setErr(apiError(e.response?.data?.detail) || "Soubor se nepodařilo přečíst."); }
    finally { setBusy(false); }
  };
  const vyber = (f) => { if (!f) return; setFile(f); rozbor(f); };
  const zmenMapovani = (pole, v) => {
    const m = { ...mapping, [pole]: v === "" ? undefined : Number(v) };
    setMapping(m); rozbor(file, analyza?.sheet, m);
  };

  const radky = par?.radky || [];
  const podleI = useMemo(() => Object.fromEntries(rows.map((r) => [r._i, r])), [rows]);
  const fn = (FILTRY.find(([k]) => k === filtr) || FILTRY[0])[2];
  const viditelne = radky.filter(fn);
  const vybraneRadky = radky.filter((x) => vybrane[x.i] && lzeVybrat(x));
  const prepsat = vybraneRadky.filter((x) => x.polozka);
  const pridat = vybraneRadky.filter((x) => !x.polozka);
  const hromadne = (zapnout) => setVybrane((o) => {
    const n = { ...o };
    viditelne.filter(lzeVybrat).forEach((x) => { n[x.i] = zapnout; });
    return n;
  });

  const potvrdit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/api/cenik-dodavatele/potvrzeni", {
        dodavatel: dodavatel.trim(), soubor: file?.name || "", kategorie_id: kat || null,
        marze_pct: Number(String(marze).replace(",", ".")) || 0,
        radky: vybraneRadky.map((x) => {
          const r = podleI[x.i] || {};
          return x.polozka
            ? { akce: "PREPSAT", polozka_id: x.polozka.id, cena_nakup: x.cena_nova, cena_prodej: r.cena_prodej ?? null }
            : { akce: "PRIDAT", cena_nakup: x.cena_nova, cena_prodej: r.cena_prodej ?? null, nazev: r.nazev || "", katalogove_cislo: r.katalogove_cislo || "",
              ean: r.ean || "", mj: r.mj || "ks", typ: r.typ === "PRACE" ? "PRACE" : "MATERIAL", vyrobce_id: x.vyrobce_navrh || null };
        }),
      });
      setVysledek(data); setVraceno(null); setStep(3); onDone?.(); nactiDavky();
    } catch (e) { setErr(apiError(e.response?.data?.detail) || "Uložení se nezdařilo."); }
    finally { setBusy(false); }
  };

  const vratit = async (id) => {
    if (!window.confirm("Vrátit import ceníku?\n\nPřepsané ceny se vrátí a položky, které import přidal, se smažou.")) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/api/cenik-dodavatele/davky/${id}/vratit`);
      setVraceno(data); onDone?.(); nactiDavky();
    } catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const s = par?.souhrn;
  const dlazdice = s ? [
    ["ZDRAZILO", "Zdražilo", s.ZDRAZILO, s.prumer_zdrazeni_pct != null ? `průměrně ${pct(s.prumer_zdrazeni_pct)}` : "", "text-danger"],
    ["ZLEVNILO", "Zlevnilo", s.ZLEVNILO, "", "text-success"],
    ["BEZE_ZMENY", "Beze změny", s.BEZE_ZMENY + s.BEZ_CENY, s.BEZ_CENY ? `${s.BEZ_CENY} bez ceny` : "", "text-ink"],
    ["NOVA", "Nové v souboru", s.NOVA, "v ceníku nejsou", "text-accent-hover"],
  ] : [];
  const tlacitko = [prepsat.length && `přepsat ${prepsat.length} ${prepsat.length === 1 ? "cenu" : prepsat.length < 5 ? "ceny" : "cen"}`,
    pridat.length && `přidat ${pridat.length} ${pridat.length === 1 ? "položku" : pridat.length < 5 ? "položky" : "položek"}`]
    .filter(Boolean).join(" a ");

  return (
    <div className="fixed inset-0 z-[70] flex md:items-start justify-center md:p-6 md:pt-8" data-testid="cenik-dodavatele">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />
      <section role="dialog" aria-label="Ceník dodavatele"
        className={`relative bg-white w-full flex flex-col shadow-panel md:rounded-card md:max-h-[calc(100vh-56px)] h-full md:h-auto ${step === 2 ? "md:max-w-[1040px]" : "md:max-w-[720px]"}`}>
        <div className="border-b border-line-soft px-4 md:px-[22px] py-3 md:py-4 flex items-center gap-3">
          <div className="min-w-0 flex-1 flex flex-col md:flex-row md:items-center md:gap-3">
            <h2 className="text-[17px] md:text-[16px] font-800">Ceník dodavatele</h2>
            <span className="t-meta text-sec-3">Krok {step} ze 3{step === 2 ? " · Kontrola" : step === 3 ? " · Hotovo" : ""}</span>
          </div>
          {file && step > 1 && (
            <span className="hidden md:inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-pill bg-surface text-sec t-meta max-w-[300px]">
              <FileText size={13} className="shrink-0" /><span className="truncate">{file.name}</span>
            </span>
          )}
          <button onClick={onClose} aria-label="Zavřít" data-testid="cenik-dodavatele-zavrit"
            className="tap md:min-h-0 md:min-w-0 h-9 w-9 rounded-full inline-flex items-center justify-center text-ink hover:bg-inputbg"><X size={19} /></button>
        </div>
        {err && <div className="mx-4 md:mx-[22px] mt-3 rounded-input bg-[#fdecea] text-danger t-meta px-3 py-2" data-testid="cenik-dodavatele-chyba">{err}</div>}

        {step === 1 && (
          <div className="overflow-y-auto flex-1 p-4 md:p-[22px] flex flex-col gap-3">
            {ai ? (
              <div className="py-12 flex flex-col items-center gap-3 text-center">
                <div className="relative"><Sparkles size={40} className="text-accent" />
                  <Loader2 size={20} className="text-accent animate-spin absolute -right-2 -bottom-1" /></div>
                <div className="t-telo font-700 text-ink">Čtu ceník…</div>
                <div className="t-meta text-muted max-w-[360px]">AI prochází PDF a vytahuje položky a ceny. U delšího ceníku to chvíli trvá — rychlejší je Excel.</div>
              </div>
            ) : (<>
              <label className="flex flex-col gap-1">
                <span className="label-up text-muted">Dodavatel</span>
                <input className="field w-full md:max-w-[360px] text-[16px] md:text-[13px]" value={dodavatel} maxLength={120}
                  onChange={(e) => setDodavatel(e.target.value)} placeholder="např. Ptáček — doplní se ze souboru" data-testid="cenik-dodavatel" />
              </label>
              <button onClick={() => fileRef.current?.click()} data-testid="cenik-dodavatele-soubor" disabled={busy}
                className="w-full border-2 border-dashed border-line-strong rounded-card py-10 flex flex-col items-center gap-2 hover:border-accent hover:bg-inputbg transition-colors">
                {busy ? <Spinner /> : <UploadCloud size={34} className="text-muted" />}
                <span className="t-telo font-600 text-ink">Vyber ceník od dodavatele</span>
                <span className="t-meta text-muted">Excel nebo CSV (max 20 MB). Tabulku z PDF přečteme hned, sken přečte AI.</span>
              </button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden"
                onChange={(e) => vyber(e.target.files?.[0])} data-testid="cenik-dodavatele-input" />
              <p className="t-meta text-muted">Porovnáme ceny s ceníkem podle katalogového čísla, EAN a názvu. Ukážeme, co zdražilo
                a co zlevnilo, a přepíšeme jen to, co potvrdíš. Prodejní cena se dopočítá z marže položky; hotové nabídky se nemění.</p>
              {davky.length > 0 && (
                <div className="mt-2" data-testid="cenik-dodavatele-historie">
                  <div className="label-up text-muted mb-1.5">Poslední importy</div>
                  {davky.slice(0, 6).map((d) => (
                    <div key={d.id} className="flex items-center gap-3 py-2 border-t border-line-softer">
                      <div className="min-w-0 flex-1">
                        <div className="t-telo font-600 text-ink truncate">{d.dodavatel || d.soubor || "Ceník dodavatele"}</div>
                        <div className="t-meta text-muted">{datum(d.created_at)} · {d.kdo} · {d.pocet_zmen} cen ({d.zdrazilo} ↑ {d.zlevnilo} ↓) · {d.pocet_novych} nových</div>
                      </div>
                      {d.vraceno_at ? <span className="t-meta text-muted shrink-0">vráceno</span> : (
                        <button onClick={() => vratit(d.id)} disabled={busy} data-testid={`davka-vratit-${d.id}`}
                          className="tap md:min-h-0 shrink-0 t-meta font-700 text-sec hover:text-ink inline-flex items-center gap-1"><RotateCcw size={13} /> Vrátit</button>
                      )}
                    </div>
                  ))}
                  {vraceno && <p className="t-meta text-success mt-1">Vráceno {vraceno.vraceno_cen} cen, smazáno {vraceno.smazano} položek.{vraceno.ponechano.length ? ` Ponecháno: ${vraceno.ponechano.join("; ")}.` : ""}</p>}
                </div>
              )}
            </>)}
          </div>
        )}

        {step === 2 && par && (<>
          <div className="overflow-y-auto flex-1">
            <div className="px-4 md:px-[22px] pt-3 md:pt-4 flex flex-col gap-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="cenik-dodavatele-souhrn">
                {dlazdice.map(([k, l, n, pod, barva]) => (
                  <button key={k} type="button" onClick={() => setFiltr(k)} data-on={filtr === k}
                    className={`text-left rounded-[12px] border-[1.5px] px-3 py-2.5 ${filtr === k ? "border-ink bg-inputbg" : "border-line-soft bg-white"}`}>
                    <div className="label-up text-muted">{l}</div>
                    <div className={`amount text-[20px] font-800 ${n ? barva : "text-muted-2"}`}>{n}</div>
                    <div className="t-meta text-muted truncate">{pod || " "}</div>
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 min-w-0">
                  <span className="label-up text-muted shrink-0">Dodavatel</span>
                  <input className="field h-9 w-[200px] min-w-0 text-[16px] md:text-[13px]" value={dodavatel} maxLength={120}
                    onChange={(e) => setDodavatel(e.target.value)} data-testid="cenik-dodavatel-2" />
                </label>
                <span role="group" aria-label="Ceny v souboru" className="inline-flex items-center rounded-pill border border-line-strong p-0.5" data-testid="cd-ceny">
                  {[["NAKUP", "Nákupní ceny"], ["VYROBCE", "Ceník výrobce"]].map(([k, l]) => (
                    <button key={k} type="button" data-on={cenyMode === k} onClick={() => { setCenyMode(k); porovnej(surove, dodavatel, k); }}
                      title={k === "VYROBCE" ? "Ceny v souboru jsou prodejní (MOC) — nákup = prodej − sleva od dodavatele" : "Ceny v souboru jsou nákupní"}
                      className={`tap md:min-h-0 h-8 px-3 rounded-pill t-meta font-700 ${cenyMode === k ? "bg-ink text-white" : "text-sec hover:text-ink"}`}
                      data-testid={`cd-ceny-${k}`}>{l}</button>
                  ))}
                </span>
                {cenyMode === "VYROBCE" && (
                  <label className="flex items-center gap-2">
                    <span className="label-up text-muted shrink-0">Sleva</span>
                    <input className="field h-9 w-[76px] amount text-[16px] md:text-[13px]" inputMode="decimal" value={slevaDod} placeholder="%"
                      onChange={(e) => setSlevaDod(e.target.value)} onBlur={() => porovnej(surove, dodavatel)}
                      onKeyDown={(e) => e.key === "Enter" && porovnej(surove, dodavatel)}
                      aria-label="Sleva od dodavatele v %" data-testid="cd-sleva" />
                    <span className="t-meta text-muted">%</span>
                  </label>
                )}
                {analyza && (<>
                  {analyza.sheets?.length > 1 && (
                    <Vyber className="field h-9 max-w-[200px]" value={analyza.sheet || ""} onChange={(e) => rozbor(file, e.target.value)} aria-label="List">
                      {analyza.sheets.map((x) => <option key={x} value={x}>{nazevListu(x)}</option>)}
                    </Vyber>
                  )}
                  <button onClick={() => setShowMapping((v) => !v)} className="btn-ghost h-9 px-2.5 t-meta" data-testid="cenik-dodavatele-mapovani">
                    <Sliders size={14} /> Sloupce
                  </button>
                </>)}
              </div>
              {showMapping && analyza && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-card bg-inputbg p-3">
                  {POLE.map(([f, l]) => (
                    <label key={f} className="flex items-center gap-2 min-w-0">
                      <span className="t-meta w-[84px] shrink-0">{l}</span>
                      <Vyber className="field min-w-0 w-full" value={mapping[f] ?? ""} onChange={(e) => zmenMapovani(f, e.target.value)}>
                        <option value="">— nemapovat —</option>
                        {(analyza.columns || []).map((c, i) => <option key={i} value={i}>{c}</option>)}
                      </Vyber>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] -mx-4 px-4 md:mx-0 md:px-0">
                {FILTRY.map(([k, l, f]) => (
                  <button key={k} onClick={() => { setFiltr(k); setLimit(NA_STRANKU); }} data-on={filtr === k} data-testid={`cd-filtr-${k}`}
                    className={`cip filtr shrink-0 ${filtr === k ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
                    {l}<span className="filtr-pocet">{radky.filter(f).length}</span>
                  </button>
                ))}
                {viditelne.some(lzeVybrat) && (<>
                  <button onClick={() => hromadne(true)} className="tap md:min-h-0 shrink-0 ml-auto t-meta font-700 text-accent-hover px-1">Vybrat vše</button>
                  <button onClick={() => hromadne(false)} className="tap md:min-h-0 shrink-0 t-meta font-700 text-sec px-1">Zrušit výběr</button>
                </>)}
              </div>
            </div>

            <div className="mt-2 border-t border-line-soft">
              {!viditelne.length && <p className="px-4 md:px-[22px] py-6 t-meta text-muted">V tomhle filtru nic není.</p>}
              {viditelne.slice(0, limit).map((x) => {
                const r = podleI[x.i] || {};
                const [stavText, stavBarva] = STAV[x.stav];
                const zvolitelne = lzeVybrat(x);
                return (
                  <label key={x.i} data-testid={`cd-radek-${x.i}`}
                    className={`flex items-start md:items-center gap-3 px-4 md:px-[22px] py-2.5 border-b border-line-softer ${zvolitelne ? "cursor-pointer hover:bg-inputbg" : ""}`}>
                    <input type="checkbox" className="h-5 w-5 mt-0.5 md:mt-0 shrink-0 accent-ink" disabled={!zvolitelne}
                      checked={!!vybrane[x.i] && zvolitelne} onChange={(e) => setVybrane((o) => ({ ...o, [x.i]: e.target.checked }))} />
                    <span className="min-w-0 flex-1">
                      <span className="block t-telo font-600 text-ink truncate">{x.polozka?.name || r.nazev}</span>
                      <span className="block t-meta text-muted truncate amount">
                        {[r.katalogove_cislo || x.polozka?.katalogove_cislo, r.cena_prodej != null && `prodej ${fmtKc(r.cena_prodej)} Kč`,
                          x.polozka?.vyrobce, x.polozka?.dodavatel && `naposledy ${x.polozka.dodavatel}`,
                          x.duplicita && "stejná položka je v souboru víckrát", x.jina_mj && `jiná jednotka: soubor ${r.mj}, ceník ${x.polozka?.mj}`]
                          .filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block amount t-telo text-ink whitespace-nowrap">
                        {x.polozka ? <><span className="text-muted line-through decoration-1">{fmtKc(x.polozka.cena_nakup)}</span> → </> : null}
                        <b>{fmtKc(x.cena_nova)}</b> Kč
                      </span>
                      <span className={`block t-meta font-700 ${stavBarva}`}>{x.rozdil_pct != null && x.stav !== "BEZE_ZMENY" ? pct(x.rozdil_pct) : stavText}</span>
                    </span>
                  </label>
                );
              })}
              {viditelne.length > limit && (
                <button onClick={() => setLimit((l) => l + NA_STRANKU)} className="w-full py-3 t-meta font-700 text-accent-hover">
                  Zobrazit dalších {Math.min(NA_STRANKU, viditelne.length - limit)} z {viditelne.length - limit}
                </button>
              )}
            </div>
            {pridat.length > 0 && (
              <div className="px-4 md:px-[22px] py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 bg-inputbg" data-testid="cd-nove-nastaveni">
                <span className="t-meta font-700 text-ink">Nové položky ({pridat.length})</span>
                <label className="flex items-center gap-2">
                  <span className="t-meta text-muted shrink-0">Technologie</span>
                  <Vyber className="field h-9 min-w-0" value={kat} onChange={(e) => setKat(e.target.value)}>
                    <option value="">—</option>
                    {kategorie.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </Vyber>
                </label>
                <label className="flex items-center gap-2">
                  <span className="t-meta text-muted shrink-0">Výchozí marže %</span>
                  <input className="field h-9 w-[90px] amount text-[16px] md:text-[13px]" inputMode="decimal" value={marze}
                    onChange={(e) => setMarze(e.target.value)} placeholder="0" data-testid="cd-marze" />
                </label>
              </div>
            )}
          </div>
          <div className="border-t border-line-soft px-4 md:px-[22px] py-3 flex items-center gap-3 pb-[calc(12px+env(safe-area-inset-bottom))] md:pb-3">
            <button onClick={() => setStep(1)} className="btn-ghost hidden md:inline-flex">Zpět</button>
            <span className="hidden md:block t-meta text-muted">{tlacitko ? `Vybráno: ${tlacitko}` : "Nic není vybráno"}</span>
            <Button onClick={potvrdit} disabled={busy || !tlacitko} className="flex-1 md:flex-none md:ml-auto !h-12 md:!h-10" data-testid="cd-potvrdit">
              {busy ? <Spinner /> : tlacitko ? tlacitko[0].toUpperCase() + tlacitko.slice(1) : "Nic není vybráno"}
            </Button>
          </div>
        </>)}

        {step === 3 && vysledek && (
          <div className="overflow-y-auto flex-1 p-4 md:p-[22px] flex flex-col gap-3" data-testid="cd-hotovo">
            <div className="flex items-center gap-2.5">
              <CheckCircle2 size={22} className="text-success shrink-0" />
              <span className="t-sekce font-700 text-ink">
                {vraceno ? "Import je vrácený" : `Přepsáno ${vysledek.pocet_zmen} cen, přidáno ${vysledek.pocet_novych} položek`}
              </span>
            </div>
            {!vraceno && <p className="t-meta text-muted">{vysledek.zdrazilo} zdražilo, {vysledek.zlevnilo} zlevnilo. Propojené skladové položky mají novou cenu taky; každá změna je v historii cen položky.</p>}
            {vraceno && <p className="t-meta text-sec">Vráceno {vraceno.vraceno_cen} cen, smazáno {vraceno.smazano} položek.{vraceno.ponechano.length ? ` Ponecháno: ${vraceno.ponechano.join("; ")}.` : ""}</p>}
            <div className="flex flex-col-reverse md:flex-row gap-2 md:justify-end mt-2">
              {!vraceno && <Button variant="ghost" onClick={() => vratit(vysledek.id)} disabled={busy} data-testid="cd-vratit"><RotateCcw size={14} /> Vrátit import</Button>}
              <Button onClick={onClose} className="!h-12 md:!h-10" data-testid="cd-zavrit">Hotovo</Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
